import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import * as util from '../../../util';
import Coord from './coord';
import Confidence from '../confidence';
import PaymentCodes from '../payment/paymentCodes';
import ModalPromo from '../modals/promo';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test.store';
import CartItems from './cart-items';
import * as emitter from '../../../global/emitter';

declare var modalOpen: any;
declare const collapse: {
    init: () => void;
};
declare const closeAllModals: () => void;

interface ICartSummaryProps {
    isMobile: boolean;
}

@observer
class CartSummaryComponent extends React.Component<ICartSummaryProps> {
    @observable randInt: number;
    @observable editingPromo: boolean;

    constructor(props) {
        super(props);
        this.randInt = Math.floor(Math.random() * 1000000001);
        this.handleCheckoutTypeChange = this.handleCheckoutTypeChange.bind(this);
        this.resetCart = this.resetCart.bind(this);
        this.reloadPayment = this.reloadPayment.bind(this);
        this.reloadPaymentFailure = this.reloadPaymentFailure.bind(this);
        this.refreshStuff = this.refreshStuff.bind(this);
        this.saveEstPostal = this.saveEstPostal.bind(this);
        this.editingPromo = false;
        makeObservable(this);
    }

    abForceUpdate = async () => this.forceUpdate();

    componentDidMount(): void {
        collapse.init();
        store.checkoutRefreshCS = this.refreshStuff;
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);
    }

    @action
    saveEstPostal = async () => {
        store.uiCheckout.isLoadingEstimated = true;
        const success = await checkout('get-est-postal');
        store.uiCheckout.isLoadingEstimated = false;
        try {
            store.checkoutRefreshCS();
        } catch {
            const x = 1;
        }
        closeAllModals();
    };

    @computed get cartItemTotal() {
        return store.checkout.cart.items.map(o => o.qty).reduce((a, b) => a + b, 0);
    }

    @computed get freeShipping() {
        if (store.checkout.cart.pricing.discount_shipping > 0) {
            return store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production - store.checkout.cart.pricing.discount_shipping === 0;
        }
        return false;
    }

    @computed get showApplePay() {
        return (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    @computed get EstPostalCode() {
        return (store.checkout.ship.addr.postalCode.value.trim() === '' ? store.checkout.ship.estimatedPostalCode : store.checkout.ship.addr.postalCode.value).trim();
    }

    @computed get showEstBox() {
        return store.checkout.cart.pricing.isEstShipping && store.checkout.ship.estimatedPostalCode === '';
    }

    @computed get superSaverDiscount() {
        try {
            return isNaN(store.checkout.ship.shipMethod.SuperSaverDiscount) ? -1 : store.checkout.ship.shipMethod.SuperSaverDiscount;
        } catch {
            return 0;
        }
    }

    @computed get getVolumeDiscount(): number {
        try {
            let _total: number = 0;
            for (var i = 0; i < store.checkout.cart.items.length; i++) {
                _total += store.checkout.cart.items[i].VolumeDiscountAmt;
            }
            return _total;
        } catch {
            return 0;
        }
    }

    @action
    resetCart = async () => {
        store.initCheckout();
        store.uiCheckout.requireReset = true;
        store.moveStep(ICheckoutStep.cart);
        this.forceUpdate();
    };

    @action
    handleCheckoutTypeChange = async (e: ICheckoutType) => {
        if (store.checkout.force_normal_checkout) {
            store.checkout.CheckoutType = ICheckoutType.normal;
            store.moveStep(ICheckoutStep.checkout);
            return;
        }

        store.checkout.CheckoutType = e;
        store.checkout.force_normal_checkout = true;
        // if (store.checkout.CheckoutType === ICheckoutType.paypal_express || store.checkout.CheckoutType === ICheckoutType.stripe_express){
        //     store.checkout.force_normal_checkout = true;
        // }
        store.moveStep(ICheckoutStep.checkout);
    };

    @action
    reloadPayment = async () => {
        this.editingPromo = false;
        try {
            store.checkoutReloadPayment();
        } catch (err) {
            const x = 1;
        }
        this.forceUpdate();
    };

    @action
    reloadPaymentFailure = async () => {
        this.editingPromo = true;
        try {
            store.checkoutReloadPayment();
        } catch (err) {
            const x = 1;
        }
        this.forceUpdate();
    };

    @action
    refreshStuff = async () => {
        this.forceUpdate();
    };

    @action
    triggerFocus = (id: string): void => {
        setTimeout(() => {
            const element = document.querySelector(id) as HTMLElement;

            if (element) {
                element.focus();
            }
        }, 100);
    };

    render() {
        collapse.init();

        return (
            <div
                className={this.props.isMobile ? 'cs cs-mobile' : 'cs'}
                style={{border: '1px solid #EAEAEA'}}
            >
                {this.props.isMobile && (
                    <div className="cs-mobile-header">
                        <div className="top">{store.checkout.cstep === ICheckoutStep.cart ? 'Your Cart' : 'Checkout'}</div>
                        {/* <div className='desc'>{this.cartItemTotal} items: <span className='price'>{util.currency(this.total)}</span></div> */}
                    </div>
                )}
                <div>
                    <ModalPromo
                        reloadPaymentFailure={this.reloadPaymentFailure}
                        reloadPayment={this.reloadPayment}
                        modalId="modal-checkout-promo-cs"
                    />
                </div>

                <div className={this.props.isMobile && store.checkout.cstep > ICheckoutStep.cart ? 'toggle-content' : ''}>
                    <div
                        className={this.props.isMobile ? 'cs-header pointer' : 'cs-header'}
                        {...(store.checkout.cart.items && store.checkout.cart.items.length > 0 && {'aria-controls': `cartSummaryCollapse${this.randInt.toString()}`})}
                    >
                        {!this.props.isMobile ? 'Order Summary' : 'Order Summary'}
                        {!this.props.isMobile && store.checkout.cstep !== ICheckoutStep.cart && !store.uiCheckout.processing && (
                            <button
                                className="cs-edit link text-btn text-uppercase b"
                                type="button"
                                onClick={() => store.moveStep(ICheckoutStep.cart)}
                                aria-label="Back to cart"
                            >
                                Back to Cart
                            </button>
                        )}
                    </div>

                    {store.checkout.cart.items && store.checkout.cart.items.length > 0 && (
                        <div
                            id={`cartSummaryCollapse${this.randInt.toString()}`}
                            className={this.props.isMobile && store.checkout.cstep > ICheckoutStep.cart ? 'collapse' : ''}
                        >
                            <div className="cs-flex">
                                <div>
                                    Subtotal ({this.cartItemTotal.toString()} {this.cartItemTotal > 1 ? 'items' : 'item'}){this.showEstBox && <div className="hint pad-bottom">Total does not include shipping or tax</div>}
                                </div>
                                <div aria-label="Subtotal">{util.currency(store.checkout.cart.pricing.subtotal + this.getVolumeDiscount)}</div>
                            </div>

                            {store.checkout.cart.pricing.discount !== 0 && (
                                <div className="cs-flex">
                                    {store.checkout.cart.pricing.discount < 0 && <div>Add ({store.checkout.code_promo_text})</div>}
                                    {store.checkout.cart.pricing.discount >= 0 && store.checkout.code_promo_text.length > 0 && <div>Discount ({store.checkout.code_promo_text})</div>}
                                    {store.checkout.cart.pricing.discount >= 0 && store.checkout.code_promo_text.length <= 0 && <div>Discount</div>}
                                    <div aria-label="Discount">
                                        {store.checkout.cart.pricing.discount < 0 && <span>{util.currency(store.checkout.cart.pricing.discount * -1)}</span>}
                                        {store.checkout.cart.pricing.discount >= 0 && <span>-{util.currency(store.checkout.cart.pricing.discount)}</span>}
                                    </div>
                                </div>
                            )}

                            {store.checkout.cart.pricing.discount !== 0 && (
                                <div className="cs-flex">
                                    <div className="b">Subtotal After {store.checkout.cart.pricing.discount < 0 ? 'Add' : 'Discount'}</div>
                                    <div
                                        className="b"
                                        aria-label="Subtotal after discount"
                                    >
                                        <span>{store.checkout.cart.pricing.subtotal - store.checkout.cart.pricing.discount < 0 ? util.currency(0) : util.currency(store.checkout.cart.pricing.subtotal - store.checkout.cart.pricing.discount)}</span>
                                    </div>
                                </div>
                            )}

                            {/* <div className='cs-flex'>
                            isEstShipping {store.checkout.cart.pricing.isEstShipping.toString()}
                            <br />
                            showEstBox {this.showEstBox.toString()}
                        </div> */}

                            {!this.showEstBox && store.checkout.cart.pricing.isEstShipping && (
                                <div
                                    className="cs-flex"
                                    style={{fontSize: 12}}
                                >
                                    {this.EstPostalCode === '' && (
                                        <div>
                                            Ship to:{' '}
                                            {store.checkout.cart.pricing.isEstShipping && store.checkout.cstep < ICheckoutStep.shipping && (
                                                <span
                                                    className="link pointer"
                                                    style={{paddingLeft: 4, fontSize: 13}}
                                                    tabIndex={0}
                                                    onClick={() => modalOpen('modal-shipping-est-postal')}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            modalOpen('modal-shipping-est-postal');
                                                        }
                                                    }}
                                                >
                                                    ({this.EstPostalCode === '' ? 'change' : `${this.EstPostalCode} (change)`})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {this.EstPostalCode !== '' && (
                                        <div>
                                            Ship to: {this.EstPostalCode}
                                            {store.checkout.cstep < ICheckoutStep.shipping && (
                                                <span
                                                    className="link pointer"
                                                    style={{fontSize: 13, paddingLeft: 4}}
                                                    tabIndex={0}
                                                    onClick={() => modalOpen('modal-shipping-est-postal')}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            modalOpen('modal-shipping-est-postal');
                                                        }
                                                    }}
                                                >
                                                    change
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div></div>
                                </div>
                            )}
                            {!this.showEstBox && store.checkout.cart.pricing.isEstShipping && (
                                <div
                                    className="cs-flex"
                                    style={{paddingTop: 0, minHeight: '24px'}}
                                >
                                    <div>Estimated Shipping</div>
                                    <div aria-label="Estimated Shipping">
                                        {!store.uiCheckout.isLoadingEstimated && (
                                            <div>
                                                {(this.freeShipping || store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production <= 0) && 'FREE'}
                                                {!this.freeShipping && store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production > 0 && util.currency(store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!this.showEstBox && store.checkout.cart.pricing.isEstShipping && (
                                <div
                                    className="cs-flex"
                                    style={{paddingTop: 0, minHeight: '24px'}}
                                >
                                    <div>Estimated Tax</div>
                                    <div
                                        aria-label="Estimated Tax"
                                        role="region"
                                        style={{minWidth: '50px', textAlign: 'right'}}
                                    >
                                        {!store.uiCheckout.isLoadingEstimated && util.currency(store.checkout.cart.pricing.tax)}
                                    </div>
                                </div>
                            )}

                            {!this.showEstBox && !store.checkout.cart.pricing.isEstShipping && (
                                <div className="cs-flex">
                                    <div>Shipping{this.freeShipping && ' (Discount)'}</div>
                                    {(!store.checkout.isMyFrames || store.checkout.myframes_isDropShip) && (
                                        <div aria-label="Shipping Cost">
                                            {!store.uiCheckout.isLoadingEstimated &&
                                                !this.freeShipping &&
                                                store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production > 0 &&
                                                util.currency(store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production + this.superSaverDiscount)}
                                            {!store.uiCheckout.isLoadingEstimated && (this.freeShipping || store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production <= 0) && 'FREE'}
                                        </div>
                                    )}
                                    {store.checkout.isMyFrames && !store.checkout.myframes_isDropShip && <div aria-label="Shipping Cost">TBD</div>}
                                </div>
                            )}

                            {store.checkout.cart.pricing.discount_shipping !== 0 && !this.freeShipping && store.checkout.code_promo_type === ISpecialDiscountCodeType.freeshipping && (
                                <div className="cs-flex">
                                    <div>Shipping Discount ({store.checkout.code_promo_shipping_text})</div>
                                    <div aria-label="Shipping Discount">-{util.currency(store.checkout.cart.pricing.discount_shipping)}</div>
                                </div>
                            )}

                            {!store.uiCheckout.isLoadingEstimated &&
                                store.checkout.code_promo_type === ISpecialDiscountCodeType.freeshipping &&
                                store.checkout.ship.shipMethod != undefined &&
                                store.checkout.ship.shipMethod.isSuperSaver &&
                                !(store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production <= 0) &&
                                this.superSaverDiscount > 0 && (
                                    <div className="cs-flex">
                                        <div>Shipping Discount (Shipping Saver)</div>
                                        <div aria-label="Shipping Discount">-{util.currency(this.superSaverDiscount)}</div>
                                    </div>
                                )}

                            {!this.showEstBox && !store.checkout.cart.pricing.isEstShipping && (
                                <div className="cs-flex">
                                    <div>Tax</div>
                                    {(!store.checkout.isMyFrames || store.checkout.myframes_isDropShip) && (
                                        <div aria-label="Tax">
                                            {!store.uiCheckout.isLoadingEstimated && util.currency(store.checkout.cart.pricing.tax)}
                                            {store.uiCheckout.isLoadingEstimated && (
                                                <span>
                                                    <i className="fas fa-circle-notch fa-spin fast-spin fa-spin-14"></i>
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {store.checkout.isMyFrames && !store.checkout.myframes_isDropShip && <div aria-label="Shipping Cost">TBD</div>}
                                </div>
                            )}

                            {this.showEstBox && (
                                <div className="cs-flex">
                                    <div>
                                        {store.checkout.cart.pricing.isEstShipping && store.checkout.cstep < ICheckoutStep.shipping && (
                                            <span
                                                className="link pointer underline"
                                                style={{fontSize: 14}}
                                                tabIndex={0}
                                                onClick={() => modalOpen('modal-shipping-est-postal')}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        modalOpen('modal-shipping-est-postal');
                                                    }
                                                }}
                                            >
                                                Estimate Shipping &amp; Tax
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        aria-label="Estimated Tax"
                                        role="region"
                                    >
                                        {!store.uiCheckout.isLoadingEstimated && store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production > 0 && util.currency(store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production)}
                                    </div>
                                </div>
                            )}

                            {store.checkout.cart.pricing.gift !== 0 && (
                                <div className="cs-flex">
                                    <div>Applied Gift Card</div>
                                    <div aria-label="Applied Gift Card Credit">-{util.currency(store.checkout.cart.pricing.gift)}</div>
                                </div>
                            )}

                            {this.getVolumeDiscount > 0 && (
                                <div className="cs-flex">
                                    <div>Volume Discount</div>
                                    <div
                                        className="volume-cs"
                                        aria-label="Volume Discount"
                                    >
                                        -{util.currency(this.getVolumeDiscount)}
                                    </div>
                                </div>
                            )}

                            {!this.editingPromo && (
                                <div
                                    className="cs-flex"
                                    style={{paddingTop: 12}}
                                >
                                    <div></div>
                                    <div>
                                        {!store.checkout.code_promo_applied && (
                                            <button
                                                className="link text-btn b"
                                                onClick={() => (this.editingPromo = !this.editingPromo)}
                                                type="button"
                                                aria-label="Promo Code"
                                            >
                                                + Promo Code{' '}
                                                <span style={{paddingLeft: 4}}>
                                                    <i
                                                        className="fas fa-tags"
                                                        aria-hidden={true}
                                                    />
                                                </span>
                                            </button>
                                        )}
                                        {store.checkout.code_promo_applied && (
                                            <button
                                                className="link text-btn"
                                                onClick={() => (this.editingPromo = !this.editingPromo)}
                                                type="button"
                                                aria-label="Promo Code"
                                            >
                                                Promo Code ({store.checkout.code_promo.value}){' '}
                                                <span style={{paddingLeft: 4}}>
                                                    <i
                                                        className="fas fa-tags"
                                                        aria-hidden={true}
                                                    />
                                                </span>
                                            </button>
                                        )}

                                        {store.checkout.code_promo_applied &&
                                            store.checkout.code_promo_type === ISpecialDiscountCodeType.freeshipping &&
                                            store.checkout.code_promo_shipping_text != undefined &&
                                            store.checkout.code_promo_shipping_text.length > 0 &&
                                            store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production > 0 &&
                                            store.checkout.cstep <= ICheckoutStep.shipping && <div style={{fontWeight: 'normal'}}>* Shipping discount ({store.checkout.code_promo_shipping_text}) will be applied after shipping information is entered.</div>}
                                    </div>
                                </div>
                            )}

                            {this.editingPromo && (
                                <div
                                    className="cs-flex"
                                    style={{paddingTop: 12}}
                                >
                                    <div>
                                        <PaymentCodes
                                            reloadPayment={this.reloadPayment}
                                            reloadPaymentFailure={this.reloadPaymentFailure}
                                            promoOnly={true}
                                        />
                                    </div>
                                </div>
                            )}

                            {!this.showEstBox && (
                                <div className="cs-flex total">
                                    <div
                                        className="form-inline-label"
                                        style={{fontSize: 18}}
                                    >
                                        {store.checkout.cart.pricing.isEstShipping ? 'Estimated ' : ''}
                                        Total
                                    </div>
                                    <div aria-label="Total Cost">
                                        {!store.uiCheckout.isLoadingEstimated && util.currency_noNegatives(store.getTotal)}
                                        {store.uiCheckout.isLoadingEstimated && (
                                            <i
                                                className="fas fa-circle-notch fa-spin fast-spin fa-spin-14 black"
                                                aria-hidden={true}
                                            ></i>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!this.props.isMobile && store.checkout.cstep <= ICheckoutStep.cart && (
                                <div className="cs-btn">
                                    <div className="hr"></div>
                                    <div style={{paddingTop: 0}}>
                                        <button
                                            type="button"
                                            className={`btn-checkout btn-checkout-yellow${store.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}${AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) ? ' btn-checkout-blue' : ''}`}
                                            onClick={() => {
                                                this.handleCheckoutTypeChange(ICheckoutType.normal);
                                                this.triggerFocus('#checkout-as-guest');
                                            }}
                                            style={{marginTop: 0}}
                                            aria-label="Checkout"
                                        >
                                            <span className="pad-right">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                            {AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) ? 'Secure Checkout' : 'Checkout'}
                                        </button>
                                    </div>
                                    {!store.checkout.isMyFrames && !AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) && (
                                        <>
                                            <div
                                                className="text-center"
                                                style={{paddingTop: 6}}
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/cc3.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        className="cc3"
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/cc3.png`}
                                                        width={216}
                                                        height={35}
                                                        alt="Most credit cards accepted"
                                                    />
                                                </picture>
                                            </div>
                                            <div className="hr"></div>
                                            <div
                                                className="d-none d-md-block"
                                                style={{marginBottom: 10}}
                                            >
                                                - or -
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn-checkout btn-checkout-white"
                                                    onClick={() => this.handleCheckoutTypeChange(ICheckoutType.paypal_express)}
                                                >
                                                    <picture>
                                                        <source
                                                            srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.webp`}
                                                            type="image/webp"
                                                        />
                                                        <img
                                                            src={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.png`}
                                                            width={100}
                                                            height={26}
                                                            alt="Checkout with PayPal "
                                                        />
                                                    </picture>
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) && (
                                        <button
                                            type="button"
                                            className={`btn-checkout btn-checkout-yellow${store.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                            style={{textTransform: 'uppercase', marginTop: 10}}
                                            onClick={() => window.open('https://www.paypal.com/us/digital-wallet/ways-to-pay/buy-now-pay-later')}
                                        >
                                            Pay in installments with
                                            <picture style={{marginLeft: 10, marginTop: -5}}>
                                                <source
                                                    srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.webp`}
                                                    type="image/webp"
                                                />
                                                <img
                                                    src={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.png`}
                                                    width={70}
                                                    height="auto"
                                                    alt="Pay in installments with"
                                                />
                                            </picture>
                                        </button>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_apple && (
                                        <div style={{paddingTop: 12}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Apple Pay"
                                            >
                                                <i className="fab fa-apple-pay"></i>
                                            </button>
                                        </div>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_google && (
                                        <div style={{paddingTop: 12}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Google Pay"
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/gpay.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        style={{maxHeight: 20, width: 'auto'}}
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/gpay.png`}
                                                        width={50}
                                                        height={26}
                                                        aria-hidden={true}
                                                        alt=""
                                                    />
                                                </picture>
                                            </button>
                                        </div>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_venmo && (
                                        <div style={{paddingTop: 12}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Venmo"
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/venmo_payment.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        style={{maxHeight: 20, width: 'auto'}}
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/venmo_payment.png`}
                                                        width={131}
                                                        height={26}
                                                        aria-hidden={true}
                                                        alt=""
                                                    />
                                                </picture>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {this.props.isMobile && store.checkout.cstep <= ICheckoutStep.cart && (
                                <div
                                    className="cs-btn"
                                    style={{marginBottom: 24}}
                                >
                                    <div className="hr"></div>
                                    <div style={{paddingTop: 0}}>
                                        <button
                                            type="button"
                                            className={`btn-checkout btn-checkout-yellow${store.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}${AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) ? ' btn-checkout-blue' : ''}`}
                                            onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                            style={{marginTop: 6}}
                                            aria-label="Checkout"
                                        >
                                            <span className="pad-right">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                            Checkout
                                        </button>
                                        {!store.checkout.myframes_isDropShip && !store.checkout.isMyFrames && (
                                            <div
                                                className="text-center"
                                                style={{marginTop: 6}}
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/cc3.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        className="checkout-cc-mobile cc3"
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/cc3.png`}
                                                        width={216}
                                                        height={35}
                                                        style={{paddingLeft: 12}}
                                                        alt="Most credit cards accepted"
                                                    />
                                                </picture>
                                            </div>
                                        )}
                                    </div>
                                    {!store.checkout.isMyFrames && <div style={{marginTop: 6}}>- Or Check Out With -</div>}

                                    {!store.checkout.isMyFrames && (
                                        <div style={{paddingTop: 6}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.paypal_express)}
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/pp_logo_h_100x26.png`}
                                                        width={100}
                                                        height={26}
                                                        alt="Checkout with PayPal "
                                                    />
                                                </picture>
                                            </button>
                                        </div>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_apple && (
                                        <div style={{paddingTop: 6}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Apple Pay"
                                            >
                                                <i className="fab fa-apple-pay"></i>
                                            </button>
                                        </div>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_google && (
                                        <div style={{paddingTop: 6}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Google Pay"
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/gpay.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        style={{maxHeight: 20, width: 'auto'}}
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/gpay.png`}
                                                        width={50}
                                                        height={26}
                                                        aria-hidden={true}
                                                        alt=""
                                                    />
                                                </picture>
                                            </button>
                                        </div>
                                    )}

                                    {!store.checkout.isMyFrames && store.payment_allow_venmo && (
                                        <div style={{paddingTop: 6}}>
                                            <button
                                                type="button"
                                                className="btn-checkout btn-checkout-white"
                                                onClick={() => this.handleCheckoutTypeChange(ICheckoutType.normal)}
                                                aria-label="Checkout with Venmo"
                                            >
                                                <picture>
                                                    <source
                                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/venmo_payment.webp`}
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        style={{maxHeight: 20, width: 'auto'}}
                                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/venmo_payment.png`}
                                                        width={131}
                                                        height={26}
                                                        aria-hidden={true}
                                                        alt=""
                                                    />
                                                </picture>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(store.checkout.cstep > ICheckoutStep.cart || this.props.isMobile) && (
                                <CartItems
                                    id={this.props.isMobile ? 100 : 50}
                                    isMobile={this.props.isMobile}
                                />
                            )}

                            <div className="d-none d-md-block">
                                <Confidence />
                            </div>
                        </div>
                    )}

                    {this.props.isMobile && store.checkout.cart.items && store.checkout.cart.items.length > 0 && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                fontSize: 14,
                                marginTop: 16,
                            }}
                        >
                            <div>
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`https://${store.Portal.CDNDomain}/images/cached/general/ppa25.webp`}
                                    />
                                    <img
                                        width="94"
                                        height="26"
                                        loading="lazy"
                                        src={`https://${store.Portal.CDNDomain}/images/cached/general/ppa25.jpg`}
                                        alt="PayPal Pay Later"
                                        style={{height: 16, width: 'auto'}}
                                    />
                                </picture>
                            </div>

                            <div style={{fontSize: 14, paddingLeft: 6, marginTop: 2}}>
                                Pay in 4 payments of {util.currency(store.checkout.cart.pricing.total / 4)}.{' '}
                                <a
                                    href="https://www.paypal.com/us/digital-wallet/ways-to-pay/buy-now-pay-later"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                    style={{fontSize: 14, textDecoration: 'underline'}}
                                >
                                    Learn More.
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                {this.props.isMobile && store.checkout.cart.items && store.checkout.cart.items.length > 0 && store.checkout.cstep === ICheckoutStep.cart && (
                    <div
                        className="continue-shopping text-center"
                        style={{paddingBottom: 12}}
                    >
                        <div className="hr" />
                        <a
                            href={store.checkout.cart.items[store.checkout.cart.items.length - 1].StoreURL}
                            aria-label="Continue Shopping"
                        >
                            Continue Shopping {store.checkout.cart.items.length > 0 && store.checkout.cart.items[0].title1}
                        </a>
                    </div>
                )}
                {this.props.isMobile && store.checkout.cart.items && store.checkout.cart.items.length > 0 && store.PortalID < 1 && store.checkout.cstep === ICheckoutStep.cart && <Coord />}

                {this.props.isMobile && (!store.checkout.cart.items || store.checkout.cart.items.length < 1) && (
                    <div
                        className="text-center"
                        style={{paddingTop: 24, paddingBottom: 24}}
                    >
                        <div
                            className="form-inline-label"
                            style={{fontSize: 28}}
                        >
                            Your cart is empty.
                        </div>
                        {store.PortalID === 3 && (
                            <div
                                className="link"
                                style={{marginTop: 36}}
                            >
                                <a
                                    href="/"
                                    title="Diploma Frames"
                                >
                                    Visit our Framerly Shop
                                </a>
                            </div>
                        )}
                        {store.PortalID === 0 && (
                            <div
                                className="link"
                                style={{marginTop: 36}}
                            >
                                <a
                                    href="/shop-frames-gifts.aspx"
                                    title="Diploma Frames"
                                    aria-label="Visit our Shop Frames and Gifts page"
                                >
                                    Visit our Shop Frames and Gifts page
                                </a>
                            </div>
                        )}
                        {store.PortalID === 0 && (
                            <div
                                className="link"
                                style={{marginTop: 36}}
                            >
                                <a
                                    href="/frame-designer"
                                    title="Custom Frame"
                                    aria-label="Custom Frame"
                                >
                                    Create your own Custom Frame
                                </a>
                            </div>
                        )}
                        {store.PortalID === 0 && (
                            <div
                                className="link"
                                style={{marginTop: 36}}
                            >
                                <a
                                    href="/about-our-frames/frames-custom-made-for-you.aspx"
                                    title="About Our Frames"
                                    aria-label="About Our Frames"
                                >
                                    Learn more about our frames
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {!this.props.isMobile && (!store.checkout.cart.items || store.checkout.cart.items.length < 1) && (
                    <div
                        className="text-center"
                        style={{paddingTop: 24, paddingBottom: 24}}
                    >
                        <div
                            className="form-inline-label"
                            style={{fontSize: 20}}
                        >
                            Empty cart
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
export default CartSummaryComponent;
